/**
 * Signup trial chrome: 90-day banner, first-charge date and the plan picked on the landing.
 * Trial length lives in the Stripe Pricing Table; this module only mirrors it in copy.
 */

/** Currencies whose Pricing Table is confirmed with the 90-day trial. */
export const SIGNUP_TRIAL_DAYS_BY_CURRENCY = Object.freeze({ BRL: 90 });

export const SIGNUP_PLAN_LABELS = Object.freeze({
  basic: "Básico",
  professional: "Profissional",
  premium: "Premium",
});

export const SIGNUP_CYCLE_LABELS = Object.freeze({
  monthly: "Mensal",
  annual: "Anual",
});

/**
 * @param {string} currency
 * @param {Record<string, number>} [overrides] CONFIG.SIGNUP_TRIAL_DAYS_BY_CURRENCY
 * @returns {number} 0 when the currency has no confirmed trial
 */
export function trialDaysForCurrency(currency, overrides) {
  const table = { ...SIGNUP_TRIAL_DAYS_BY_CURRENCY, ...(overrides || {}) };
  const days = Number(table[String(currency || "").toUpperCase()]);
  return Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
}

/**
 * @param {Date} now
 * @param {number} [days]
 * @returns {Date}
 */
export function computeTrialEndDate(now, days = 90) {
  const end = new Date(now.getTime());
  end.setDate(end.getDate() + days);
  return end;
}

/**
 * @param {Date} date
 * @param {string} [locale]
 * @returns {string} e.g. "7 de janeiro de 2027"
 */
export function formatTrialEndDate(date, locale = "pt-BR") {
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/**
 * Plan/cycle chosen on the landing (`?plan=professional&cycle=annual`), whitelisted.
 * @param {string} search
 * @returns {{ plan: string, cycle: string|null }|null}
 */
export function readLandingSelection(search) {
  const params = new URLSearchParams(search || "");
  const plan = String(params.get("plan") || "").toLowerCase();
  if (!Object.prototype.hasOwnProperty.call(SIGNUP_PLAN_LABELS, plan)) {
    return null;
  }
  const cycle = String(params.get("cycle") || "").toLowerCase();
  return {
    plan,
    cycle: Object.prototype.hasOwnProperty.call(SIGNUP_CYCLE_LABELS, cycle) ? cycle : null,
  };
}

/** UTM keys forwarded by the landing; mirrors backend `ACQUISITION_KEYS`. */
export const SIGNUP_UTM_KEYS = Object.freeze([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
]);

const ACQUISITION_MAX_LENGTH = 100;
const SIGNUP_ACQUISITION_STORAGE_KEY = "lc_signup_acquisition";

function cleanAcquisitionValue(value) {
  return Array.from(String(value || ""))
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code >= 0x20 && code !== 0x7f;
    })
    .join("")
    .trim()
    .slice(0, ACQUISITION_MAX_LENGTH)
    .trim();
}

/**
 * Marketing attribution from the signup URL: UTMs plus the landing plan/cycle.
 * @param {string} search
 * @returns {Record<string, string>} empty when nothing was found
 */
export function readAcquisitionParams(search) {
  const params = new URLSearchParams(search || "");
  /** @type {Record<string, string>} */
  const acquisition = {};
  SIGNUP_UTM_KEYS.forEach((key) => {
    const value = cleanAcquisitionValue(params.get(key));
    if (value) acquisition[key] = value;
  });
  const selection = readLandingSelection(search);
  if (selection) {
    acquisition.landing_plan = selection.plan;
    if (selection.cycle) acquisition.landing_cycle = selection.cycle;
  }
  return acquisition;
}

/**
 * URL values win; otherwise reuse what was captured earlier in this tab
 * (survives a reload after the query string is lost).
 * @param {string} search
 * @returns {Record<string, string>}
 */
export function captureSignupAcquisition(search) {
  const fromUrl = readAcquisitionParams(search);
  try {
    if (Object.keys(fromUrl).length) {
      window.sessionStorage.setItem(SIGNUP_ACQUISITION_STORAGE_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
    const stored = JSON.parse(
      window.sessionStorage.getItem(SIGNUP_ACQUISITION_STORAGE_KEY) || "{}"
    );
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return {};
    /** @type {Record<string, string>} */
    const restored = {};
    [...SIGNUP_UTM_KEYS, "landing_plan", "landing_cycle"].forEach((key) => {
      const value = cleanAcquisitionValue(stored[key]);
      if (value) restored[key] = value;
    });
    return restored;
  } catch {
    return fromUrl;
  }
}

/**
 * @param {{ plan: string, cycle: string|null }} selection
 * @returns {string}
 */
export function planSelectionLabel(selection) {
  const plan = SIGNUP_PLAN_LABELS[selection.plan];
  const cycle = selection.cycle ? SIGNUP_CYCLE_LABELS[selection.cycle] : "";
  return cycle ? `Plano escolhido: ${plan} · ${cycle}` : `Plano escolhido: ${plan}`;
}

/**
 * Toggle trial-only vs. generic copy and fill the first-charge date.
 * @param {{ currency: string, now?: Date, overrides?: Record<string, number> }} options
 * @returns {number} trial days applied (0 = generic copy)
 */
export function applySignupTrialChrome({ currency, now = new Date(), overrides } = {}) {
  const days = trialDaysForCurrency(currency, overrides);
  const hasTrial = days > 0;

  document.querySelectorAll("[data-signup-trial-only]").forEach((el) => {
    el.classList.toggle("hidden", !hasTrial);
  });
  document.querySelectorAll("[data-signup-generic-only]").forEach((el) => {
    el.classList.toggle("hidden", hasTrial);
  });

  if (hasTrial) {
    const label = formatTrialEndDate(computeTrialEndDate(now, days));
    document.querySelectorAll("[data-signup-trial-end-date]").forEach((el) => {
      el.textContent = label;
    });
    document.querySelectorAll("[data-signup-trial-days]").forEach((el) => {
      el.textContent = String(days);
    });
  }
  return days;
}

const SIGNUP_CURRENCY_STORAGE_KEY = "lc_signup_currency";

/**
 * Stripe checkout returns to signup-sucesso.html in the same tab; the success page
 * has no other way to know which Pricing Table (currency) was used.
 * @param {string} currency
 */
export function rememberSignupCurrency(currency) {
  try {
    window.sessionStorage.setItem(SIGNUP_CURRENCY_STORAGE_KEY, String(currency || ""));
  } catch {
    // Storage blocked: success page falls back to generic copy.
  }
}

/**
 * @returns {string} "" when unknown
 */
export function readRememberedSignupCurrency() {
  try {
    return window.sessionStorage.getItem(SIGNUP_CURRENCY_STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

/**
 * Fill `[data-signup-support-link="calendar|whatsapp"]` from CONFIG.
 * @param {{ ONBOARDING_CALENDAR_URL?: string, SALES_WHATSAPP_URL?: string }|undefined} config
 */
export function applySignupSupportLinks(config) {
  const urls = {
    calendar: config?.ONBOARDING_CALENDAR_URL || "",
    whatsapp: config?.SALES_WHATSAPP_URL || "",
  };
  document.querySelectorAll("[data-signup-support-link]").forEach((el) => {
    const url = urls[el.getAttribute("data-signup-support-link")];
    if (url && /^https:\/\//.test(url)) {
      el.setAttribute("href", url);
      el.classList.remove("hidden");
    } else {
      el.classList.add("hidden");
    }
  });
}

/**
 * Show the "Plano escolhido" chip when the visitor came from a landing plan card.
 * @param {string} search
 * @returns {{ plan: string, cycle: string|null }|null}
 */
export function renderLandingSelection(search) {
  const chip = document.getElementById("signupPlanChoice");
  const selection = readLandingSelection(search);
  if (!chip) return selection;
  if (!selection) {
    chip.classList.add("hidden");
    return null;
  }
  const label = chip.querySelector("[data-signup-plan-choice-label]");
  if (label) label.textContent = planSelectionLabel(selection);
  chip.dataset.plan = selection.plan;
  chip.classList.remove("hidden");
  return selection;
}
